<template>
  <v-card flat>
    <v-card-text>
      <!-- Threshold Selector -->
      <v-row class="mb-2" align="center">
        <v-col cols="12" sm="4" md="3">
          <v-select
            v-model="daysThreshold"
            :items="thresholdOptions"
            item-text="label"
            item-value="value"
            label="No sales within"
            outlined
            dense
          />
        </v-col>
        <v-col cols="auto">
          <span class="caption grey--text">
            Shows items that have stock but have not sold within the selected
            period.
          </span>
        </v-col>
      </v-row>

      <!-- Summary Cards -->
      <v-row v-if="report" class="mb-4">
        <v-col cols="6" sm="4">
          <v-card color="amber darken-2" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">{{ report.total_count }}</div>
            <div class="caption">Slow/Dead Stock Items</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4">
          <v-card color="red darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h5 font-weight-bold">
              {{ formatCurrency(report.total_cost_tied_up) }}
            </div>
            <div class="caption">Capital Tied Up</div>
          </v-card>
        </v-col>
        <v-col cols="6" sm="4">
          <v-card color="blue darken-1" dark class="pa-3 text-center" flat>
            <div class="text-h6 font-weight-bold">
              {{ report.days_threshold }} days
            </div>
            <div class="caption">Threshold</div>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table -->
      <v-data-table
        v-if="report"
        :items="report.items"
        :headers="headers"
        :search="search"
        item-key="id"
        dense
        :items-per-page="25"
        :footer-props="{ 'items-per-page-options': [10, 25, 50, -1] }"
      >
        <template v-slot:top>
          <v-text-field
            v-model="search"
            prepend-icon="mdi-magnify"
            label="Search items..."
            dense
            class="mb-2"
          />
        </template>
        <template v-slot:item.quantity="{ item }">
          <v-chip color="amber darken-2" dark small>{{ item.quantity }}</v-chip>
        </template>
        <template v-slot:item.days_since_last_sale="{ item }">
          <v-chip :color="getAgeColor(item.days_since_last_sale)" dark small>
            {{
              item.days_since_last_sale !== null
                ? item.days_since_last_sale + " days"
                : "Never"
            }}
          </v-chip>
        </template>
        <template v-slot:item.unit_price="{ item }">
          {{ formatCurrency(item.unit_price) }}
        </template>
        <template v-slot:item.selling_price="{ item }">
          {{ formatCurrency(item.selling_price) }}
        </template>
        <template v-slot:item.inventory_cost_tied_up="{ item }">
          <span class="red--text font-weight-bold">
            {{ formatCurrency(item.inventory_cost_tied_up) }}
          </span>
        </template>
        <template v-slot:item.batch_name="{ item }">
          {{ item.batch_name || "-" }}
        </template>
        <template v-slot:item.branch="{ item }">
          {{ item.branch ? item.branch.branch_name : "-" }}
        </template>
      </v-data-table>

      <!-- Empty State -->
      <div v-if="!report" class="text-center grey--text pa-10">
        <v-icon size="64" color="grey lighten-1">mdi-trending-down</v-icon>
        <p class="mt-4 subtitle-1">
          Click "Generate" to identify slow-moving and dead stock.
        </p>
        <p class="caption grey--text">
          Finds items with stock on hand that have not sold within the threshold
          period.
        </p>
      </div>

      <!-- No Results -->
      <div
        v-if="report && report.items && report.items.length === 0"
        class="text-center pa-10"
      >
        <v-icon size="64" color="green">mdi-check-circle</v-icon>
        <p class="mt-4 subtitle-1 green--text">
          All items with stock have recent sales. No slow-moving items found.
        </p>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapActions } from "vuex";
export default {
  name: "SlowMovingReport",
  props: {
    filters: { type: Object, required: true },
  },
  data() {
    return {
      report: null,
      search: "",
      daysThreshold: 30,
      thresholdOptions: [
        { label: "Last 14 days", value: 14 },
        { label: "Last 30 days", value: 30 },
        { label: "Last 60 days", value: 60 },
        { label: "Last 90 days", value: 90 },
        { label: "Last 180 days", value: 180 },
      ],
      headers: [
        { text: "Item Name", value: "item_name" },
        { text: "Type", value: "item_type" },
        { text: "Supplier", value: "supplier" },
        { text: "Batch", value: "batch_name" },
        { text: "Stock on Hand", value: "quantity", align: "center" },
        { text: "Last Sold", value: "last_sold_date" },
        {
          text: "Days Since Sale",
          value: "days_since_last_sale",
          align: "center",
        },
        { text: "Unit Cost", value: "unit_price", align: "end" },
        { text: "Selling Price", value: "selling_price", align: "end" },
        { text: "Cost Tied Up", value: "inventory_cost_tied_up", align: "end" },
        { text: "Branch", value: "branch" },
      ],
    };
  },
  methods: {
    ...mapActions({
      fetchReport: "inventory_reports/get_slow_moving_report",
    }),
    formatCurrency(value) {
      if (!value && value !== 0) return "0.00";
      return Number(value).toLocaleString("en-PH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    getAgeColor(days) {
      if (days === null) return "red darken-3";
      if (days >= 180) return "red darken-3";
      if (days >= 90) return "red";
      if (days >= 60) return "orange darken-1";
      if (days >= 30) return "amber darken-1";
      return "yellow darken-2";
    },
    async generate() {
      try {
        let params = { days_threshold: this.daysThreshold };
        if (this.filters.item_type_id)
          params.item_type_id = this.filters.item_type_id;
        if (this.filters.supplier) params.supplier = this.filters.supplier;
        if (this.filters.branch_id) params.branch_id = this.filters.branch_id;
        this.report = await this.fetchReport(params);
      } catch (e) {
        this.$alert({
          title: "Error",
          message:
            "Error generating slow moving report: " +
            (e.response?.data?.error || e.message),
          color: "error",
          icon: "mdi-alert-circle",
        });
      }
    },
  },
};
</script>
